// The Tamagui config, compiled from the derived token set.
//
// §15 chose Tamagui for one specific reason — "§15 is a token problem. The
// palette, type scale, spacing, and motion durations above are exactly Tamagui's
// createTokens / createTheme model, so the design system is not a document that
// the code drifts away from — it IS the config the components compile against."
//
// This file is the join. It contains no design decisions of its own: every value
// comes from theme.ts, which in turn contains no value that is not in
// tokens.json. Two hops, both mechanical, and the share-card generator reads the
// same file at the far end.

import { createFont, createTamagui, createTokens } from 'tamagui';
import { defaultConfig } from '@tamagui/config/v4';
import { monoFont, sansFont, tamaguiTokens, themes } from './theme';

/**
 * `mono` is the interface font and `sans` is for user content.
 *
 * §15 is blunt about the split: monospace throughout, "never for user content:
 * player comments, country names, and anything a person wrote render in a clean
 * sans, unstyled, because dressing user text in the theme makes it illegible".
 * Registering both here is what makes the wrong one a visible choice rather than
 * an accident — a component that wants sans has to ask for it by name.
 */
export const fonts = {
  mono: createFont(monoFont),
  body: createFont(monoFont),
  heading: createFont(monoFont),
  sans: createFont(sansFont),
};

export const tokens = createTokens(tamaguiTokens);

export const config = createTamagui({
  ...defaultConfig,
  fonts,
  tokens,
  themes,
  defaultFont: 'mono',
  settings: {
    ...defaultConfig.settings,
    defaultFont: 'mono',
    // Longhands stay legal. Tamagui's v4 preset turns them off in favour of `bg`
    // and `ai`, which is a fine default for an app team writing hundreds of
    // screens — and the wrong trade here, where the interface is a dozen
    // components that have to be readable against a spec section written in CSS
    // vocabulary. `backgroundColor` is what §15 says; `bg` is what we would have
    // to translate it into on every reading.
    onlyAllowShorthands: false,
    // §15 has one theme and it is dark. Emitting prefers-color-scheme rules would
    // put a light palette in the stylesheet that no part of the design defines.
    shouldAddPrefersColorThemes: false,
  },
});

export type WWPConfig = typeof config;

declare module 'tamagui' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TamaguiCustomConfig extends WWPConfig {}
}
